import React from 'react';
import { useTranslate } from '../../../components/TranslationText';
import TranslationsKeys from '../../../TranslationsKeys';
import { FormTextInput, FormFileUpload } from '../../../components/FormInputs';
import SubmitButton from '../../../components/servicesButtons/SubmitButton';
import { WorkshopRegistrationConstants } from '../utils/workshopRegistrationConstants';

/**
 * Workshop Registration Form Component.
 * Contains FullName, Email, and IdentityDocument upload fields with SubmitButton.
 */
export default function WorkshopRegistrationForm({ isSubmitting, onSubmit }) {
    const { translate } = useTranslate();

    return (
        <div className="p-main bg-white border-15 p-4 mb-main">
            <form onSubmit={onSubmit} noValidate>
                {/* Section Intro */}
                <div className="border-bottom pb-2 mb-4">
                    <h5 className="fw-bold horseCardTitle mb-1">
                        {translate(TranslationsKeys.PARTICIPANT_INFO,  'Participant Information')}
                    </h5>
                    <small className="text-muted">
                        {translate(
                            TranslationsKeys.REQUIRED_FIELDS_NOTE,'Fields marked with (*) are required'
                        )}
                    </small>
                </div>

                {/* 1. Full Name (الاسم الثلاثي) */}
                <div className="mb-3">
                    <FormTextInput
                        name={WorkshopRegistrationConstants.FULL_NAME}
                        label={translate(TranslationsKeys.FULL_NAME, 'Full Name')}
                        placeholder={translate(TranslationsKeys.ENTER_FULL_NAME, 'Enter your full name')}
                        required
                        rules={{
                            required: translate(TranslationsKeys.REQUIRED_FIELD, 'Full name is required'),
                            minLength: {
                                value: 3,
                                message: 'Name must be at least 3 characters',
                            },
                            validate: (val) =>
                                ((val && val.trim().length >= 3) ||
                                    translate(TranslationsKeys.VALID_FULL_NAME_REQUIRED, 'Please enter a valid full name')),
                        }}
                    />
                </div>

                {/* 2. Email (البريد الإلكتروني) */}
                <div className="mb-3">
                    <FormTextInput
                        name={WorkshopRegistrationConstants.EMAIL}
                        type="email"
                        label={translate(TranslationsKeys.EMAIL, 'Email Address')}
                        placeholder={translate(TranslationsKeys.ENTER_EMAIL,'example@domain.com')}
                        required
                        rules={{
                            required: translate(TranslationsKeys.REQUIRED_FIELD, 'Email address is required'),
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: translate(
                                    TranslationsKeys.INVALID_EMAIL,
                                    'Please enter a valid email address'
                                ),
                            },
                        }}
                    />
                </div>

                {/* 3. Identity Document (إرفاق الهوية) */}
                <div className="mb-4">
                    <FormFileUpload
                        name={WorkshopRegistrationConstants.IDENTITY_DOCUMENT}
                        label={translate(TranslationsKeys.IDENTITY_DOCUMENT, 'Identity Document (Emirates ID / Passport)')}
                        accept="image/*,application/pdf"
                        required
                        rules={{
                            validate: (val) => {
                                if (!val || (Array.isArray(val) && val.length === 0)) {
                                    return translate(
                                        TranslationsKeys.UPLOAD_ID_REQUIRED, 'Please upload your identity document'
                                    );
                                }
                                return true;
                            },
                        }}
                    />
                    <small className="text-muted d-block mt-1" style={{ fontSize: 12 }}>
                        <i className="fa-solid fa-circle-info me-1 ms-1"></i>
                        {translate(
                            TranslationsKeys.ALLOWED_FILES_NOTE,'Allowed formats: Images (PNG, JPG) and PDF documents, maximum 10MB'
                        )}
                    </small>
                </div>

                {/* Form Action Button (Only Submit button) */}
                <div className="d-flex justify-content-end align-items-center pt-3 border-top mt-4">
                    <SubmitButton
                        isSubmitting={isSubmitting}
                        text={translate(TranslationsKeys.SUBMIT_REGISTRATION, 'Submit Registration')}
                        loadingText={translate(TranslationsKeys.SUBMITTING,  'Submitting...')}
                    />
                </div>
            </form>
        </div>
    );
}
